import React, { useState, useEffect } from "react";
import { useTheme } from "../../../../contexts/ThemeContext.js";
import { useAuth } from "../../../../contexts/AuthContext.js";
import { useTenancy } from "../../../../contexts/TenancyContext.js";
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { deiterateAPI } from "../../../../js/external-js/variableModules/utils/deiterateAPI.util";
import documentsService from "../../../../services/documentsService";
import slackIcon from '../../../../assets/img/slack.png';
import { getAvailableFeatures } from "@/utils/featuresUtil";

interface AtlassianIntegration {
  atlassianAPIKey?: {
    value?: string;
    cloudSecretVersionName?: string;
  };
  atlassianURL?: string;
  atlassianUserEmail?: string;
}

interface SharePointAuthStatus {
  isAuthenticated: boolean;
  userEmail?: string;
  tenantId?: string;
  expiresAt?: string;
  sharePointUser?: {
    displayName: string;
    mail: string;
    userPrincipalName: string;
    id: string;
    jobTitle?: string;
    officeLocation?: string;
    businessPhones?: string[];
    mobilePhone?: string;
  };
  tenant?: {
    domain: string;
    tenantId: string;
  };
}

interface SharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
  drives: SharePointDrive[];
}

interface SharePointDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

interface SharePointFile {
  id: string;
  name: string;
  size: number;
  downloadUrl: string;
  webUrl: string;
  lastModifiedDateTime: string;
  createdDateTime: string;
  mimeType: string;
  parentReference: {
    driveId: string;
    id: string;
    path: string;
  };
}

interface IntegrationsData {
  atlassian?: AtlassianIntegration;
}

export default function IntegrationsSection() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();

  const availableFeatures = getAvailableFeatures();
  const hasSharePoint = !!availableFeatures.find(f => f.name === 'SharePoint');

  const [integrations, setIntegrations] = useState<IntegrationsData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    atlassianAPIKey: '',
    atlassianURL: '',
    atlassianUserEmail: ''
  });

  // SharePoint integration state
  const [sharePointAuthStatus, setSharePointAuthStatus] = useState<SharePointAuthStatus>({ isAuthenticated: false });
  const [sharePointSites, setSharePointSites] = useState<SharePointSite[]>([]);
  const [sharePointFiles, setSharePointFiles] = useState<SharePointFile[]>([]);
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<SharePointDrive | null>(null);
  const [isSharePointLoading, setIsSharePointLoading] = useState(false);
  const [sharePointError, setSharePointError] = useState<string | null>(null);
  const [sharePointTenantId, setSharePointTenantId] = useState('');
  const [testResults, setTestResults] = useState<string[]>([]);

  const db = getFirestore();

  // Helper function to encrypt data using RSA with Web Crypto API
  const encryptWithRSA = async (publicKeyPem: string, data: string): Promise<ArrayBuffer> => {
    try {
      // Convert PEM to binary
      const pemHeader = "-----BEGIN PUBLIC KEY-----";
      const pemFooter = "-----END PUBLIC KEY-----";
      const pemContents = publicKeyPem
        .replace(pemHeader, "")
        .replace(pemFooter, "")
        .replace(/\s/g, "");

      // Convert from base64 to binary
      const binaryDer = Uint8Array.from(atob(pemContents), c => c.charCodeAt(0));

      // Import the key
      const publicKey = await crypto.subtle.importKey(
        "spki",
        binaryDer,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        false,
        ["encrypt"]
      );

      // Encrypt the data
      const encoded = new TextEncoder().encode(data);
      const encrypted = await crypto.subtle.encrypt(
        {
          name: "RSA-OAEP",
        },
        publicKey,
        encoded
      );

      return encrypted;
    } catch (error) {
      console.error('Error encrypting with RSA:', error);
      throw new Error('Failed to encrypt data with RSA');
    }
  };

  // Load integrations data from Firestore and Google Cloud Secrets
  useEffect(() => {
    const loadIntegrationsData = async () => {
      if (!activeTenancy?.id || !user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch integrations document from Firestore
        const integrationsRef = doc(db, 'customers', activeTenancy.id, 'companySettings', 'integrations');
        const integrationsDoc = await getDoc(integrationsRef);

        if (integrationsDoc.exists()) {
          const data = integrationsDoc.data() as IntegrationsData;
          setIntegrations(data);

          // Populate form with existing data
          const atlassian = data.atlassian || {};
          setFormData({
            atlassianURL: atlassian.atlassianURL || '',
            atlassianUserEmail: atlassian.atlassianUserEmail || '',
            atlassianAPIKey: '' // Don't pre-populate for security
          });

          // If there's an API key, try to fetch it from Google Cloud Secrets
          if (atlassian.atlassianAPIKey?.cloudSecretVersionName) {
            try {
              const secretData = await deiterateAPI.POST('/deiterateInternalAPI/settings/getAPISecret', {
                body: JSON.stringify({
                  customerID: activeTenancy.id,
                  secretKey: 'atlassian.atlassianAPIKey'
                }),
                companyID: activeTenancy.id
              });

              if (secretData?.secret?.value) {
                setFormData(prev => ({ ...prev, atlassianAPIKey: secretData.secret.value }));
              }
            } catch (error) {
              console.log('ℹ️ No existing API key found in secrets (this is normal for new setups):', error.message || error);
              // Don't throw - this is expected for new integrations
            }
          }
        }
      } catch (error) {
        console.error('Error loading integrations data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadIntegrationsData();
  }, [activeTenancy?.id, user, db]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save integration data
  const handleSave = async () => {
    if (!activeTenancy?.id || !user) return;

    try {
      setIsSaving(true);

      const updatedIntegrations: IntegrationsData = {
        ...integrations,
        atlassian: {
          ...integrations.atlassian,
          atlassianURL: formData.atlassianURL,
          atlassianUserEmail: formData.atlassianUserEmail,
          // Only update API key if a new one was provided
          ...(formData.atlassianAPIKey ? {
            atlassianAPIKey: {
              ...integrations.atlassian?.atlassianAPIKey,
              value: formData.atlassianAPIKey
            }
          } : {})
        }
      };

      // Save to Firestore
      const integrationsRef = doc(db, 'customers', activeTenancy.id, 'companySettings', 'integrations');
      await setDoc(integrationsRef, updatedIntegrations, { merge: true });

      // If API key was updated, encrypt and save to Google Cloud Secrets
      if (formData.atlassianAPIKey) {
        try {
          // Use native Web Crypto API for encryption (React-compatible)
          const publicKeyPem = (window as any).publicKey || import.meta.env.VITE_publicKey;

          console.log('🔍 Public key check:', {
            windowPublicKey: !!(window as any).publicKey,
            envPublicKey: !!import.meta.env.VITE_publicKey,
            publicKeyLength: publicKeyPem ? publicKeyPem.length : 0
          });

          if (!publicKeyPem || publicKeyPem.length < 100) {
            console.warn('⚠️ Public key not available or invalid. API key will be saved without encryption.');

            // Fallback: Save unencrypted (for development/testing)
            const dataToSave = {
              atlassian: {
                atlassianAPIKey: {
                  value: formData.atlassianAPIKey // Temporary: unencrypted for dev
                },
                ...(formData.atlassianURL && { atlassianURL: { value: formData.atlassianURL } }),
                ...(formData.atlassianUserEmail && { atlassianUserEmail: { value: formData.atlassianUserEmail } })
              }
            };

            const result = await deiterateAPI.POST('/deiterateInternalAPI/settings/createAPISecret', {
              body: JSON.stringify({
                customerID: activeTenancy.id,
                ...dataToSave
              }),
              companyID: activeTenancy.id
            });

            console.log('✅ Integration saved successfully (unencrypted mode):', result);
            return;
          }

          // Encrypt using native Web Crypto API
          const encryptedBuffer = await encryptWithRSA(publicKeyPem, formData.atlassianAPIKey);

          // Convert ArrayBuffer to base64 string for storage
          const encryptedAPIKey = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));

          console.log('🔐 Encryption successful:', {
            originalLength: formData.atlassianAPIKey.length,
            encryptedLength: encryptedAPIKey.length,
            encryptedPreview: encryptedAPIKey.substring(0, 50) + '...'
          });

          // Use createAPISecret endpoint with encrypted data structure
          const dataToSave = {
            atlassian: {
              atlassianAPIKey: {
                value: encryptedAPIKey,
                encrypted: true // Flag to indicate this is encrypted
              },
              ...(formData.atlassianURL && { atlassianURL: { value: formData.atlassianURL } }),
              ...(formData.atlassianUserEmail && { atlassianUserEmail: { value: formData.atlassianUserEmail } })
            }
          };

          const result = await deiterateAPI.POST('/deiterateInternalAPI/settings/createAPISecret', {
            body: JSON.stringify({
              customerID: activeTenancy.id,
              ...dataToSave
            }),
            companyID: activeTenancy.id
          });

          console.log('✅ Integration saved successfully (encrypted mode):', result);
        } catch (error) {
          console.error('❌ Error saving API key to secrets:', error);
          // Don't throw here - the Firestore save was successful

          // Show user-friendly message
          alert('Integration settings saved to local database, but there was an issue saving the API key securely. Please try again or contact support if the issue persists.');
        }
      }

      setIntegrations(updatedIntegrations);
      console.log('Integration saved successfully!');
    } catch (error) {
      console.error('Error saving integration:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // SharePoint integration functions
  const addTestResult = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setTestResults(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const clearTestResults = () => {
    setTestResults([]);
  };

  const checkSharePointAuthStatus = async () => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult('Checking SharePoint authentication status...');

      const status = await documentsService.getSharePointAuthStatus();
      setSharePointAuthStatus(status);

      if (status.isAuthenticated) {
        const userDisplay = status.sharePointUser?.displayName || status.userEmail || 'Unknown User';
        const tenantDisplay = status.tenant?.domain || 'Unknown Tenant';
        addTestResult(`✅ Authenticated as ${userDisplay}`);
        addTestResult(`   Connected to tenant: ${tenantDisplay}`);
      } else {
        addTestResult('❌ Not authenticated');
      }
    } catch (error: any) {
      const errorMsg = `Error checking auth status: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const authenticateSharePoint = async () => {
    try {
      if (!sharePointTenantId.trim()) {
        setSharePointError('Please enter a Tenant ID');
        return;
      }

      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult(`Starting SharePoint authentication for tenant: ${sharePointTenantId}`);

      const authUrl = await documentsService.getSharePointAuthUrl(sharePointTenantId);
      addTestResult('✅ Generated auth URL, opening in new window...');

      // Open auth URL in new window
      const popup = window.open(authUrl, '_blank', 'width=600,height=700');

      addTestResult('Please complete authentication in the popup window...');

      // Listen for the popup to send back the authorization code
      const handleMessage = async (event: MessageEvent) => {
        if (event.data.type === 'sharepoint_auth_success') {
          try {
            addTestResult('✅ Authorization code received, exchanging for tokens...');

            // Exchange the authorization code for tokens
            await documentsService.handleSharePointCallback(event.data.code, sharePointTenantId);

            addTestResult('✅ SharePoint authentication successful!');

            // Check auth status to update UI
            await checkSharePointAuthStatus();

            // Clean up event listener
            window.removeEventListener('message', handleMessage);
          } catch (error: any) {
            const errorMsg = `Token exchange error: ${error.message}`;
            setSharePointError(errorMsg);
            addTestResult(`❌ ${errorMsg}`);
            window.removeEventListener('message', handleMessage);
          }
        }
      };

      // Add event listener for popup messages
      window.addEventListener('message', handleMessage);

      // Clean up if popup is closed manually
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkClosed);
          addTestResult('Authentication popup was closed.');
        }
      }, 1000);
    } catch (error: any) {
      const errorMsg = `Authentication error: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const loadSharePointSites = async () => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult('Loading SharePoint sites...');

      const sites = await documentsService.getSharePointSites();
      setSharePointSites(sites);
      addTestResult(`✅ Loaded ${sites.length} SharePoint sites`);

      sites.forEach(site => {
        addTestResult(`  - ${site.displayName} (${site.drives.length} drives)`);
      });
    } catch (error: any) {
      const errorMsg = `Error loading sites: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const loadSharePointFiles = async (driveId: string, folderId?: string) => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult(`Loading files from drive: ${driveId}${folderId ? `, folder: ${folderId}` : ''}`);

      const files = await documentsService.getSharePointFiles(driveId, folderId);
      setSharePointFiles(files);
      addTestResult(`✅ Loaded ${files.length} files`);

      files.slice(0, 5).forEach(file => {
        addTestResult(`  - ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      });

      if (files.length > 5) {
        addTestResult(`  ... and ${files.length - 5} more files`);
      }
    } catch (error: any) {
      const errorMsg = `Error loading files: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  const testPullFile = async (file: SharePointFile) => {
    try {
      setIsSharePointLoading(true);
      setSharePointError(null);
      addTestResult(`Pulling file: ${file.name}`);

      const result = await documentsService.pullSharePointFile(
        file.parentReference.driveId,
        file.id,
        {
          documentType: 'Document',
          classification: 'Business Confidential',
          // componentId: 'TEST-SP'
        }
      );

      if (result.success) {
        addTestResult(`✅ Successfully pulled file: ${result.fileName}`);
        addTestResult(`  Document ID: ${result.documentId}`);
        addTestResult(`  File size: ${result.fileSize} bytes`);
      } else {
        addTestResult(`❌ Failed to pull file: ${result.message}`);
      }
    } catch (error: any) {
      const errorMsg = `Error pulling file: ${error.message}`;
      setSharePointError(errorMsg);
      addTestResult(`❌ ${errorMsg}`);
    } finally {
      setIsSharePointLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Integrations</h2>
        <p className="text-sm text-muted-foreground mb-6">Configure integrations for your tenancy.</p>

        {/* Atlassian Integration */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-500/10 p-2 rounded-lg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.9999 0C5.3726 0 0 5.37267 0 12.0001C0 18.6274 5.3726 24 11.9999 24C18.6274 24 24 18.6274 24 12.0001C24 5.37267 18.6274 0 11.9999 0ZM18.0206 11.9999C18.0206 14.7393 15.7925 16.9674 13.0532 16.9674C10.3138 16.9674 8.08578 14.7393 8.08578 11.9999C8.08578 9.26055 10.3138 7.03252 13.0532 7.03252C15.7925 7.03252 18.0206 9.26055 18.0206 11.9999ZM7.97937 11.9999C7.97937 14.7393 5.75134 16.9674 3.01196 16.9674C0.272589 16.9674 -1.95544 14.7393 -1.95544 11.9999C-1.95544 9.26055 0.272589 7.03252 3.01196 7.03252C5.75134 7.03252 7.97937 9.26055 7.97937 11.9999ZM18.0206 11.9999C18.0206 14.7393 20.2487 16.9674 22.988 16.9674C25.7274 16.9674 27.9554 14.7393 27.9554 11.9999C27.9554 9.26055 25.7274 7.03252 22.988 7.03252C20.2487 7.03252 18.0206 9.26055 18.0206 11.9999Z" fill="#0052CC" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground">Confluence</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="atlassianAPIKeyInput" className="block text-sm font-medium text-foreground">
                Confluence API Key
              </label>
              <input
                type="password"
                id="atlassianAPIKeyInput"
                value={formData.atlassianAPIKey}
                onChange={(e) => handleInputChange('atlassianAPIKey', e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground disabled:opacity-50"
                placeholder={isLoading ? "Loading..." : "Enter Confluence API key"}
              />
              <p className="text-xs text-muted-foreground">
                Create an API token using the following <a href="https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/" target="_blank" className="text-primary hover:underline">guide</a>.
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="atlassianURLInput" className="block text-sm font-medium text-foreground">
                Confluence URL
              </label>
              <input
                type="text"
                id="atlassianURLInput"
                value={formData.atlassianURL}
                onChange={(e) => handleInputChange('atlassianURL', e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground disabled:opacity-50"
                placeholder={isLoading ? "Loading..." : "Enter Confluence URL (e.g., deiterate.atlassian.net)"}
              />
              <p className="text-xs text-muted-foreground">
                Copy the hostname of your Confluence instance. For example: <i>deiterate.atlassian.net</i>
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="atlassianUserEmailInput" className="block text-sm font-medium text-foreground">
                Confluence Email
              </label>
              <input
                type="email"
                id="atlassianUserEmailInput"
                value={formData.atlassianUserEmail}
                onChange={(e) => handleInputChange('atlassianUserEmail', e.target.value)}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground disabled:opacity-50"
                placeholder={isLoading ? "Loading..." : "Enter Confluence account email"}
              />
              <p className="text-xs text-muted-foreground">
                The email of the account used to generate the Confluence API key.
              </p>
            </div>

            <div className="pt-4">
              <button
                onClick={handleSave}
                disabled={isLoading || isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
                    Saving...
                  </div>
                ) : (
                  'Save Integration'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Add more integrations here */}
        {hasSharePoint && (
          <>
            {/* SharePoint Integration Testing */}
            <div className="mt-8 pt-8 border-t border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-600 p-2 rounded-lg">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4C2 2.89543 2.89543 2 4 2H20C21.1046 2 22 2.89543 22 4V20C22 21.1046 21.1046 22 20 22H4C2.89543 22 2 21.1046 2 20V4ZM4 4V20H20V4H4ZM6 6H18V8H6V6ZM6 10H18V12H6V10ZM6 14H18V16H6V14ZM6 18H12V20H6V18Z" fill="white" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-foreground">SharePoint Integration Testing</h3>
              </div>

              <div className="space-y-6">
                {/* Tenant ID Input */}
                <div className="space-y-2">
                  <label htmlFor="sharePointTenantId" className="block text-sm font-medium text-foreground">
                    SharePoint Tenant ID
                  </label>
                  <input
                    type="text"
                    id="sharePointTenantId"
                    value={sharePointTenantId}
                    onChange={(e) => setSharePointTenantId(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    placeholder="Enter your SharePoint tenant ID (e.g., yourtenant.onmicrosoft.com)"
                  />
                  <p className="text-xs text-muted-foreground">
                    Find your tenant ID in Azure AD or use your domain (e.g., contoso.onmicrosoft.com)
                  </p>
                </div>

                {/* Authentication Section */}
                <div className="space-y-4">
                  <h4 className="font-medium text-foreground">1. Authentication</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={checkSharePointAuthStatus}
                      disabled={isSharePointLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                    >
                      Check Auth Status
                    </button>
                    <button
                      onClick={authenticateSharePoint}
                      disabled={isSharePointLoading || !sharePointTenantId.trim()}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                    >
                      Authenticate
                    </button>
                  </div>

                  {sharePointAuthStatus.isAuthenticated && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✅</span>
                        <span className="font-medium text-green-800">SharePoint Connected</span>
                      </div>

                      {sharePointAuthStatus.sharePointUser && (
                        <div className="text-sm text-green-700 space-y-1">
                          <p>
                            <span className="font-medium">User:</span> {sharePointAuthStatus.sharePointUser.displayName}
                          </p>
                          <p>
                            <span className="font-medium">Email:</span> {sharePointAuthStatus.sharePointUser.mail}
                          </p>
                          {sharePointAuthStatus.sharePointUser.jobTitle && (
                            <p>
                              <span className="font-medium">Title:</span> {sharePointAuthStatus.sharePointUser.jobTitle}
                            </p>
                          )}
                        </div>
                      )}

                      {sharePointAuthStatus.tenant && (
                        <div className="text-sm text-green-700 space-y-1 pt-2 border-t border-green-200">
                          <p>
                            <span className="font-medium">Tenant:</span> {sharePointAuthStatus.tenant.domain}
                          </p>
                          <p>
                            <span className="font-medium">Tenant ID:</span> {sharePointAuthStatus.tenant.tenantId}
                          </p>
                        </div>
                      )}

                      {sharePointAuthStatus.expiresAt && (
                        <p className="text-xs text-green-600 pt-2 border-t border-green-200">
                          <span className="font-medium">Token Expires:</span> {new Date(sharePointAuthStatus.expiresAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Sites Section */}
                {sharePointAuthStatus.isAuthenticated && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">2. Browse SharePoint Sites</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={loadSharePointSites}
                        disabled={isSharePointLoading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                      >
                        Load Sites
                      </button>
                    </div>

                    {sharePointSites.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-foreground">Select Site:</label>
                        <select
                          value={selectedSite?.id || ''}
                          onChange={(e) => {
                            const site = sharePointSites.find(s => s.id === e.target.value);
                            setSelectedSite(site || null);
                            setSelectedDrive(null);
                            setSharePointFiles([]);
                          }}
                          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                        >
                          <option value="">Select a site...</option>
                          {sharePointSites.map(site => (
                            <option key={site.id} value={site.id}>
                              {site.displayName} ({site.drives.length} drives)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedSite && selectedSite.drives.length > 0 && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-foreground">Select Drive:</label>
                        <select
                          value={selectedDrive?.id || ''}
                          onChange={(e) => {
                            const drive = selectedSite.drives.find(d => d.id === e.target.value);
                            setSelectedDrive(drive || null);
                            setSharePointFiles([]);
                          }}
                          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                        >
                          <option value="">Select a drive...</option>
                          {selectedSite.drives.map(drive => (
                            <option key={drive.id} value={drive.id}>
                              {drive.name} ({drive.driveType})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Files Section */}
                {selectedDrive && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">3. Browse and Pull Files</h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadSharePointFiles(selectedDrive.id)}
                        disabled={isSharePointLoading}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
                      >
                        Load Files
                      </button>
                    </div>

                    {sharePointFiles.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Found {sharePointFiles.length} files:</p>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {sharePointFiles.map(file => (
                            <div key={file.id} className="flex items-center justify-between p-2 border border-border rounded bg-background">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-foreground">{file.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(file.size / 1024).toFixed(1)} KB • {new Date(file.lastModifiedDateTime).toLocaleDateString()}
                                </p>
                              </div>
                              <button
                                onClick={() => testPullFile(file)}
                                disabled={isSharePointLoading}
                                className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                              >
                                Pull File
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Test Results */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-foreground">Test Results</h4>
                    <button
                      onClick={clearTestResults}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                    >
                      Clear
                    </button>
                  </div>

                  {sharePointError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">❌ {sharePointError}</p>
                    </div>
                  )}

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
                    {testResults.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No test results yet. Start by checking authentication status.</p>
                    ) : (
                      <div className="space-y-1">
                        {testResults.map((result, index) => (
                          <p key={index} className="text-xs font-mono text-gray-700">
                            {result}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {isSharePointLoading && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm">Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <div className="mt-8 pt-8 border-t border-border">
          <h3 className="text-lg font-medium text-foreground mb-4">Other Integrations - Coming Soon</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* SharePoint Integration */}
            <div className="p-4 border border-border rounded-lg bg-background hover:bg-accent/50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-transparent p-2 rounded-lg">
                  <svg className="h-8 w-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="15.5" cy="11.5" r="9.5" fill="url(#paint0_linear_87_7767)" />
                    <circle cx="24" cy="17" r="8" fill="url(#paint1_linear_87_7767)" />
                    <mask id="mask0_87_7767" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="10" y="6" width="13" height="24">
                      <path d="M23 23.5C23 27.0899 20.0899 30 16.5 30C12.9101 30 10 27.0899 10 23.5C10 19.9102 10 6 10 6H23C23 6 23 21.1988 23 23.5Z" fill="#C4C4C4" />
                    </mask>
                    <g mask="url(#mask0_87_7767)">
                      <circle cx="16.5" cy="23.5" r="6.5" fill="url(#paint2_linear_87_7767)" />
                      <path d="M7 12C7 10.3431 8.34315 9 10 9H17C18.6569 9 20 10.3431 20 12V24C20 25.6569 18.6569 27 17 27H7V12Z" fill="#000000" fill-opacity="0.3" />
                    </g>
                    <rect y="7" width="18" height="18" rx="2" fill="url(#paint3_linear_87_7767)" />
                    <path d="M13 18.1229C13 16.5726 11.9602 15.8883 9.79665 15.0922C8.10273 14.4637 7.70021 14.2821 7.70021 13.6816C7.70021 13.1648 8.20335 12.8156 9.0587 12.8156C9.93082 12.8156 10.7526 13.1089 11.6751 13.6117L12.6143 11.9497C11.6247 11.3352 10.4507 11 9.02516 11C6.84486 11 5.28512 12.1173 5.28512 13.8212C5.28512 15.567 6.52621 16.1257 8.60587 16.8659C10.2662 17.4525 10.5849 17.7458 10.5849 18.2626C10.5849 18.8771 9.9979 19.1844 9.07547 19.1844C7.98532 19.1844 7.02935 18.8073 6.07338 18.1927L5 19.7849C6.174 20.595 7.63312 21 9.12579 21C11.3732 21 13 19.9385 13 18.1229Z" fill="white" />
                    <defs>
                      <linearGradient id="paint0_linear_87_7767" x1="6" y1="11.5" x2="26.5833" y2="11.5" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#103A3B" />
                        <stop offset="1" stop-color="#116B6E" />
                      </linearGradient>
                      <linearGradient id="paint1_linear_87_7767" x1="18" y1="13" x2="32" y2="21" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#1D9097" />
                        <stop offset="1" stop-color="#29BBC2" />
                      </linearGradient>
                      <linearGradient id="paint2_linear_87_7767" x1="12" y1="21.5" x2="23" y2="26.5" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#28A6B5" />
                        <stop offset="1" stop-color="#31D6EC" />
                      </linearGradient>
                      <linearGradient id="paint3_linear_87_7767" x1="0" y1="16" x2="19.5" y2="16" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#105557" />
                        <stop offset="1" stop-color="#116B6E" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-foreground">SharePoint</h4>
                  <p className="text-xs text-muted-foreground">Connect your SharePoint Drive</p>
                </div>
              </div>
              <button className="w-full mt-2 px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-md hover:bg-primary/20 transition-colors disabled:opacity-50" disabled>
                Coming Soon
              </button>
            </div>

            {/* Slack Integration */}
            <div className="p-4 border border-border rounded-lg bg-background hover:bg-accent/50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-transparent p-2 rounded-lg">
                 <img src={slackIcon} alt="Slack" width={24} height={24} />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Slack</h4>
                  <p className="text-xs text-muted-foreground">Connect your Slack workspace</p>
                </div>
              </div>
              <button className="w-full mt-2 px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-md hover:bg-primary/20 transition-colors disabled:opacity-50" disabled>
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
